/**
 * History Data Adapter - Provides data source abstraction for history page
 *
 * Allows the history page to display data from different sources:
 * - Local database (user's own data)
 * - Remote API (monitored user's data)
 */

import { getDb, dbManager, type DafneUserDB } from "../db/user-database";
import type { CgmReading, LogEntry, UserSettings, DayNote } from "../types";
import { syncService } from "./sync/sync-service";

export interface HistoryData {
  readings: CgmReading[];
  entries: LogEntry[];
  settings?: UserSettings;
  dayNotes: DayNote[];
}

export interface HistoryDataAdapter {
  /** Load data from the source (with optional days limit for lazy loading) */
  loadData(days?: number): Promise<HistoryData>;

  /** Optional: Title to display */
  title?: string;

  /** Optional: Subtitle to display */
  subtitle?: string;

  /** Optional: Back button handler - if provided, shows back button */
  onBack?: () => void;

  /** Optional: Save a note - if provided, allows adding notes in the view */
  saveNote?(date: string, content: string): Promise<{ noteId: string }>;

  /** Whether this adapter supports adding notes */
  canAddNotes?: boolean;

  /** Whether this adapter supports lazy loading more data */
  supportsLazyLoad?: boolean;

  /** Current number of days loaded (for lazy loading) */
  loadedDays?: number;
}

/**
 * Local adapter - loads data from IndexedDB (user's own data)
 */
export class LocalHistoryAdapter implements HistoryDataAdapter {
  async loadData(): Promise<HistoryData> {
    if (!dbManager.isInitialized) {
      return { readings: [], entries: [], dayNotes: [] };
    }
    const db = getDb();

    // Load core data first - dayNotes might not exist on older DB versions
    const [readings, entries, settings] = await Promise.all([
      db.cgmReadings.toArray(),
      db.entries.toArray(),
      db.settings.get("settings"),
    ]);

    // Try to load dayNotes - table might not exist yet
    let dayNotes: DayNote[] = [];
    try {
      if (db.dayNotes) {
        const notes = await db.dayNotes.toArray();
        dayNotes = (notes || []).filter((n) => !n._deletedAt) as DayNote[];
      }
    } catch (e) {
      console.warn("dayNotes table not available yet:", e);
    }

    console.log(
      `LocalHistoryAdapter: Loaded ${readings?.length || 0} readings, ${entries?.length || 0} entries`,
    );

    return {
      readings: (readings || []).filter((r) => !r._deletedAt) as CgmReading[],
      entries: (entries || []).filter((e) => !e._deletedAt) as LogEntry[],
      settings: settings as UserSettings | undefined,
      dayNotes,
    };
  }
}

/**
 * Monitored user adapter - loads data from API for a user being monitored
 * Supports lazy loading - starts with 3 days and loads more on demand
 */
export class MonitoredUserAdapter implements HistoryDataAdapter {
  title?: string;
  subtitle?: string;
  onBack?: () => void;
  canAddNotes: boolean;
  supportsLazyLoad = true;
  loadedDays = 0;
  private userId: string;
  private cachedData: HistoryData | null = null;

  constructor(
    userId: string,
    displayName: string,
    email?: string,
    onBack?: () => void,
    relationshipType: "monitor" | "clinician" = "monitor",
  ) {
    this.userId = userId;
    this.title = displayName;
    this.subtitle = email;
    this.onBack = onBack;
    this.canAddNotes = relationshipType === "clinician";
  }

  async loadData(days: number = 3): Promise<HistoryData> {
    // If we already have data for this many days or more, return cached
    if (this.cachedData && this.loadedDays >= days) {
      return this.cachedData;
    }

    const data = await syncService.fetchMonitoredUserData(this.userId, days);

    if (!data) {
      throw new Error("Unable to access data. You may not have permission.");
    }

    // Get settings from the response (first item in array)
    const settingsArray = data.settings as unknown[];
    const settings =
      settingsArray && settingsArray.length > 0
        ? (settingsArray[0] as UserSettings)
        : undefined;

    this.cachedData = {
      readings: (data.cgmReadings || []) as CgmReading[],
      entries: (data.entries || []) as LogEntry[],
      settings,
      dayNotes: (data.dayNotes || []) as DayNote[],
    };
    this.loadedDays = days;

    return this.cachedData;
  }

  async saveNote(date: string, content: string): Promise<{ noteId: string }> {
    const result = await syncService.addMonitoredUserNote(
      this.userId,
      date,
      content,
    );
    if (!result) {
      throw new Error("Failed to save note");
    }
    return { noteId: result.noteId };
  }
}

/**
 * Local monitored-user adapter — reads from a pre-populated local Dexie DB.
 * Used by monitors and clinicians so date navigation is instant.
 * Clinicians can add notes; monitors cannot.
 */
export class LocalMonitoredAdapter implements HistoryDataAdapter {
  title?: string;
  subtitle?: string;
  canAddNotes: boolean;
  private _db: DafneUserDB;
  private _userId: string;

  constructor(
    db: DafneUserDB,
    userId: string,
    displayName: string,
    email?: string,
    relationshipType: "monitor" | "clinician" = "monitor",
  ) {
    this._db = db;
    this._userId = userId;
    this.title = displayName;
    this.subtitle = email;
    this.canAddNotes = relationshipType === "clinician";
  }

  async loadData(): Promise<HistoryData> {
    const [readings, entries, settings] = await Promise.all([
      this._db.cgmReadings.toArray(),
      this._db.entries.toArray(),
      this._db.settings.get("settings"),
    ]);

    let dayNotes: DayNote[] = [];
    try {
      if (this._db.dayNotes) {
        const notes = await this._db.dayNotes.toArray();
        dayNotes = (notes || []).filter((n) => !(n as any)._deletedAt) as DayNote[];
      }
    } catch {
      // dayNotes table may not exist
    }

    return {
      readings: (readings || []).filter((r) => !(r as any)._deletedAt) as CgmReading[],
      entries: (entries || []).filter((e) => !(e as any)._deletedAt) as LogEntry[],
      settings: settings as UserSettings | undefined,
      dayNotes,
    };
  }

  async saveNote(date: string, content: string): Promise<{ noteId: string }> {
    const result = await syncService.addMonitoredUserNote(
      this._userId,
      date,
      content,
    );
    if (!result) {
      throw new Error("Failed to save note");
    }
    return { noteId: result.noteId };
  }
}

/**
 * Sample data adapter - provides pre-generated sample data for demo purposes
 */
export class SampleDataAdapter implements HistoryDataAdapter {
  // Title/subtitle intentionally omitted so the page renders the same
  // "Let's take a look" header as the real History page. The sample-page
  // wrapper shows a banner above the page identifying this as demo data.
  canAddNotes = false;

  private readings: CgmReading[];
  private entries: LogEntry[];

  constructor(readings: CgmReading[], entries: LogEntry[]) {
    this.readings = readings;
    this.entries = entries;
  }

  async loadData(): Promise<HistoryData> {
    return {
      readings: this.readings,
      entries: this.entries,
      dayNotes: [],
    };
  }
}
